import { useMutation } from '@apollo/client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import ADD_PRODUCT_PICTURES from 'api/mutations/ADD_PRODUCT_PICTURES';
import REMOVE_PRODUCT_PICTURE from 'api/mutations/REMOVE_PRODUCT_PICTURE';
import DotsSvg from 'images/v5/icons/dots-horizontal.svg';
import EditSvg from 'images/v5/icons/edit-02.svg';
import ExpandSvg from 'images/v5/icons/expand-01.svg';
import FileCheckedSvg from 'images/v5/icons/file-check-01.svg';
import PrinterSvg from 'images/v5/icons/printer.svg';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import InfoBox from 'layout/atoms/InfoBox/InfoBox';
import Loader from 'layout/atoms/Loader/Loader';
import ParameterDetails from 'layout/atoms/ParameterDetails/ParameterDetails';
import PhotoPreview from 'layout/atoms/PhotoPreview';
import Sidebar from 'layout/atoms/Sidebar/Sidebar';
import StatusBadge from 'layout/atoms/StatusBadge';
import TabsButton from 'layout/atoms/TabsButton/TabsButton';
import Tooltip from 'layout/atoms/Tooltip';
import Grid from 'layout/molecules/Grid/Grid';
import SupplierCard from 'layout/molecules/SupplierCard/SupplierCard';
import parseImage from 'parsers/common/parseImage';
import { removePicture, replacePictures, resetComponent, selectComponentDetails, selectComponentDetailsStatus, } from 'store/componentDetailsSlice';
import { updateItemById } from 'store/componentsSlice';
import { closeSidebar, openModal, selectLayout } from 'store/layoutSlice';
import { addComponentToAssign, removeComponentToAssign, selectIsComponentAssigned, } from 'store/specificationComponentsSlice';
import getCategoryLabel from 'utils/getCategoryLabel';
import { withAttachments } from 'utils/graphql';
import AddSharedComponent from './AddSharedComponent';
import Info from './Info';
import Photos from './Photos';
import Variants from './Variants';
function ComponentPreview({ shared }) {
    var _a;
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const specView = pathname.startsWith('/specifications');
    const { t, i18n } = useTranslation(['specification', 'common']);
    const componentDetails = useSelector(selectComponentDetails);
    const status = useSelector(selectComponentDetailsStatus);
    const layoutState = useSelector(selectLayout);
    const [activeTab, setActiveTab] = useState('info');
    const [addProductPictures] = useMutation(ADD_PRODUCT_PICTURES);
    const [removeProductPicture] = useMutation(REMOVE_PRODUCT_PICTURE);
    const skipResetRef = useRef(false);
    useEffect(() => {
        const ref = skipResetRef;
        return () => {
            if (!ref.current) {
                dispatch(resetComponent());
            }
        };
    }, [dispatch]);
    const handlePhotoAdd = useCallback((chosenFiles) => {
        withAttachments(addProductPictures)({
            variables: { productId: componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id, files: chosenFiles },
        }).then((response) => {
            const allImages = response.data.addProductPictures.product.pictures.edges.map((picture) => {
                return {
                    id: picture.node.id,
                    image: picture.node.image,
                    picture: parseImage(picture.node.image),
                };
            });
            dispatch(replacePictures(allImages));
            dispatch(updateItemById({
                id: (componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id) || '',
                data: { pictures: response.data.addProductPictures.product.pictures },
            }));
        });
    }, [dispatch, addProductPictures, componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id]);
    const handlePhotoDelete = (id) => {
        removeProductPicture({ variables: { productPictureId: id } }).then(() => {
            dispatch(removePicture(id));
            dispatch(updateItemById({
                id: (componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id) || '',
                data: {
                    pictures: {
                        edges: componentDetails.pictures
                            .filter((picture) => picture.id !== id)
                            .map((picture) => {
                            return {
                                node: picture,
                            };
                        }),
                    },
                },
            }));
        });
    };
    const sidebarName = shared ? 'sharedComponentPreview' : 'componentPreview';
    const handleEditComponent = useCallback(() => {
        skipResetRef.current = true;
        dispatch(openModal({ name: 'componentEditor', id: componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id }));
    }, [dispatch, componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id]);
    const handleOpenDetails = useCallback(() => {
        const url = `/components/details/${componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id}`;
        if (specView) {
            window.open(url, '_blank');
        }
        else {
            skipResetRef.current = true;
            dispatch(closeSidebar({ name: sidebarName }));
            history.push(url);
        }
    }, [componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id, specView, dispatch, history, sidebarName]);
    const handlePdfPreview = useCallback(() => {
        dispatch(openModal({ name: 'componentPdfPreview', id: componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id }));
    }, [dispatch, componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id]);
    const isComponentAssigned = useSelector(selectIsComponentAssigned((componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id) || ''));
    const handleSelect = useCallback(() => {
        dispatch(addComponentToAssign({
            productCategoryId: componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.categoryId,
            productId: componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id,
        }));
    }, [dispatch, componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id, componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.categoryId]);
    const handleUnselect = useCallback(() => {
        dispatch(removeComponentToAssign({ productId: componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id }));
    }, [dispatch, componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id]);
    const tabs = [
        { id: 'info', label: t('info') },
        { id: 'photos', label: t('photos') },
        { id: 'supplier', label: t('supplier') },
        { id: 'variants', label: t('variants') },
    ];
    if (status.fetch === 'idle' || status.fetch === 'loading') {
        return (React.createElement(Sidebar, { name: sidebarName, header: React.createElement("div", null) },
            React.createElement(Grid, null,
                React.createElement(Loader, null))));
    }
    return (componentDetails && (React.createElement(Sidebar, { name: sidebarName, header: (componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.status) && (React.createElement(StatusBadge, { size: "l", theme: "fill", color: "positive" }, componentDetails.status === 'ACTIVE'
            ? t('status_active_alternative')
            : t('status_archived_alternative'))), headerAside: React.createElement(React.Fragment, null,
            React.createElement(Dropdown, { trigger: React.createElement(Button, { size: "s", shape: "rounded", theme: "ghost", leftIcon: React.createElement(DotsSvg, null) }) },
                React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(EditSvg, null), onClick: handleEditComponent }, t('edit')),
                React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(PrinterSvg, null), onClick: handlePdfPreview }, t('common:pdf')),
                React.createElement("hr", null),
                React.createElement(Button, { align: "left", theme: "ghost", color: "danger", leftIcon: React.createElement(TrashSvg, null), onClick: () => dispatch(openModal({ name: 'deleteComponent', id: componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id })) }, t('delete'))),
            React.createElement(Button, { size: "s", shape: "rounded", theme: "ghost", leftIcon: React.createElement(ExpandSvg, null), onClick: handleOpenDetails })) },
        React.createElement("div", { style: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
            } },
            React.createElement(ParameterDetails, { size: "xl", label: getCategoryLabel(componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.category, i18n), value: (componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.name) || '-' }),
            specView &&
                ((componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.activeProductVariantsCount) === 0 ? (React.createElement(Tooltip, { maxWidth: 180, position: "bottom", content: React.createElement(Grid, { columns: 1, gap: 12 },
                        React.createElement("p", null, t('COMPONENT_MUST_HAVE_VARIANTS_TO_BE_SELECTED'))), delay: 0 },
                    React.createElement(Button, { size: "m", color: "primary", leftIcon: React.createElement(FileCheckedSvg, null), disabled: true }, isComponentAssigned ? t('unselect_product') : t('select_product')))) : (React.createElement(Button, { size: "m", color: "primary", leftIcon: React.createElement(FileCheckedSvg, null), onClick: isComponentAssigned ? handleUnselect : handleSelect }, isComponentAssigned ? t('unselect_product') : t('select_product'))))),
        React.createElement(PhotoPreview, { src: (componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.pictures[0]) ? componentDetails.pictures[0].picture : null, radius: true }),
        React.createElement(TabsButton, { tabs: tabs, activeTab: activeTab, onClick: (e) => setActiveTab(e) }),
        shared && (React.createElement(InfoBox, { type: "info", text: t('add_product_message'), aside: React.createElement(Button, { theme: "ghost", onClick: () => dispatch(openModal({ name: 'addSharedComponent', id: componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id })) }, t('common:add')), noBorder: true })),
        {
            info: React.createElement(Info, { showPhoto: false }),
            photos: React.createElement(Photos, { photoAdd: handlePhotoAdd, photoDelete: handlePhotoDelete }),
            supplier: React.createElement(SupplierCard, { supplier: componentDetails.supplier }),
            variants: React.createElement(Variants, null),
        }[activeTab],
        layoutState.showModals.some((modal) => modal.name === 'addSharedComponent') &&
            ((_a = layoutState.showModals) === null || _a === void 0 ? void 0 : _a.find((modal) => modal.id === (componentDetails === null || componentDetails === void 0 ? void 0 : componentDetails.id))) && (React.createElement(AddSharedComponent, null)))));
}
export default ComponentPreview;
